// Angular
import { ErrorHandler, NgModule } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import { CustomRouteReuseStrategy } from './custom-route-reuse-strategy';
// Translate
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { createTranslateLoader } from './translate.loader';
// NGXS
import { NgxsModule, NoopNgxsExecutionStrategy } from '@ngxs/store';
import { NgxsStoragePluginModule, SESSION_STORAGE_ENGINE } from '@ngxs/storage-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
// import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
// Prime Ng
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
// Sentry
import * as Sentry from "@sentry/angular";
// Medsurf Lib
import { FLAT_SETTINGS } from '@medsurf/flat-services';
import * as FlatStore from '@medsurf/flat-states';
import * as FlatFacades from '@medsurf/flat-facades';
import * as FlatValidators from '@medsurf/flat-validators';
// Env
import { environment } from '../environments/environment';
// Modules
import { AppRoutingModule } from './app-routing.module';
import { ChaptersModule } from './chapters/chapters.module';
import { ElearningsModule } from './elearnings/elearnings.module';
import { ErrorsModule } from './errors/errors.module';
import { LoginModule } from './login/login.module';
import { MedsurfViewerCommonModule } from './common/medsurf-viewer-common.module';
import { PagesModule } from './pages/pages.module';
import { SlideModule } from './slide/slide.module';
import { SubjectsModule } from './subjects/subjects.module';
import { FlatDesignModule } from '@medsurf/flat-designs';
// App
import { AppComponent } from './app.component';


@NgModule({
  imports: [
    // Angular
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    // Translate
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (createTranslateLoader),
        deps: [HttpClient]
      }
    }),
    // NGXS
    NgxsModule.forRoot([
      // Flat Control Store
      FlatStore.AlertControlState,
      FlatStore.AuthControlState,
      FlatStore.HistoryControlState,
      FlatStore.MetadataControlState,
      FlatStore.NavigationControlState,
      FlatStore.NodeControlState,
      FlatStore.PageControlState,
      FlatStore.SearchControlState,
      FlatStore.SlideControlState,
      // Flat Entity Store
      FlatStore.AnnotationEntityState,
      FlatStore.AnnotationGroupEntityState,
      FlatStore.AnnotationLabelEntityState,
      FlatStore.AnnotationSourceEntityState,
      FlatStore.AnnotationTargetEntityState,
      FlatStore.ChoiceEntityState,
      FlatStore.CopyrightEntityState,
      FlatStore.FreeFormStyleEntityState,
      FlatStore.ImageEntityState,
      FlatStore.KeymapColumnEntityState,
      FlatStore.KeymapLabelEntityState,
      FlatStore.KeymapStyleEntityState,
      FlatStore.MediaEntityState,
      FlatStore.MetadataEntityState,
      FlatStore.ModalityEntityState,
      FlatStore.NodeEntityState,
      FlatStore.PageEntityState,
      FlatStore.PointOfInterestEntityState,
      FlatStore.QuestionEntityState,
      FlatStore.SlideLayerEntityState,
      FlatStore.StainEntityState,
      FlatStore.UserEntityState,
      FlatStore.VocabularyGroupEntityState,
      FlatStore.VocabularyWordEntityState,
      // Flat Shared Store
      FlatStore.ImageSharedState,
      FlatStore.TrainingSharedState,
      // Flat Viewer Store
      FlatStore.FeedbackViewerState,
      FlatStore.MenuSharedState,
      FlatStore.SlideViewerState,
      FlatStore.MediaInteractionViewerState
    ], {
      executionStrategy: NoopNgxsExecutionStrategy,
      developmentMode: false, // TODO IMPORTANT true for develop
      selectorOptions: {
        suppressErrors: true, // TODO IMPORTANT false for develop
        injectContainerState: false,
      }
    }),
    NgxsStoragePluginModule.forRoot({
      key: [
        // Flat Control Store
        'authControl.token',
        'authControl.tokenLifetime',
        'authControl.expiresIn',
        // OK@@@TODO_import TODO
        {
          key: 'trainingShared.selectedNodes',
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: 'trainingShared.selectedStartUrl',
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: 'trainingShared.typedSelectedSlideResults',
          engine: SESSION_STORAGE_ENGINE
        },
        {
          key: 'trainingShared.typedQuestionChoicesOrder',
          engine: SESSION_STORAGE_ENGINE
        },
        'menuShared.showVirtualPointer',
        'menuShared.showNavigator',
        'menuShared.showMarkers',
        'menuShared.showSelftest',
        'menuShared.showCaseHistory',
      ]
    }),
    NgxsRouterPluginModule.forRoot(),
    /* TODO IMPORTANT * /
    NgxsReduxDevtoolsPluginModule.forRoot({
      disabled: environment.production,
    }),
    /**/
    // Prime Ng
    ToastModule,
    // Modules
    AppRoutingModule,
    ChaptersModule,
    ElearningsModule,
    ErrorsModule,
    LoginModule,
    MedsurfViewerCommonModule,
    PagesModule,
    SlideModule,
    SubjectsModule,
    FlatDesignModule,
  ],
  declarations: [
    AppComponent,
  ],
  providers: [
    // Error Handlers
    {provide: ErrorHandler, useValue: Sentry.createErrorHandler({ showDialog: false })},
    // {provide: Sentry.TraceService, deps: [Router]},
    // {provide: APP_INITIALIZER, useFactory: () => () => {}, deps: [Sentry.TraceService], multi: true},
    // Prime Ng
    {provide: MessageService, useClass: MessageService},
    // Settings
    {provide: FLAT_SETTINGS, useValue: environment.config},
    {provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy},
    // Control FlatFacades
    {provide: FlatFacades.AlertControlFacade, useClass: FlatFacades.AlertControlFacade},
    {provide: FlatFacades.AuthControlFacade, useClass: FlatFacades.AuthControlFacade},
    {provide: FlatFacades.NavigationControlFacade, useClass: FlatFacades.NavigationControlFacade},
    {provide: FlatFacades.SearchControlFacade, useClass: FlatFacades.SearchControlFacade},
    // Guard FlatFacades
    {provide: FlatFacades.BaseGuardChainFacade, useClass: FlatFacades.BaseGuardChainFacade},
    // Shared FlatFacades
    {provide: FlatFacades.AppSharedFacade, useClass: FlatFacades.AppSharedFacade},
    {provide: FlatFacades.ImageSharedFacade, useClass: FlatFacades.ImageSharedFacade},
    {provide: FlatFacades.MenuSharedFacade, useClass: FlatFacades.MenuSharedFacade},
    {provide: FlatFacades.NodeSharedFacade, useClass: FlatFacades.NodeSharedFacade},
    {provide: FlatFacades.PageSharedFacade, useClass: FlatFacades.PageSharedFacade},
    {provide: FlatFacades.TrainingSharedFacade, useClass: FlatFacades.TrainingSharedFacade},
    {provide: FlatFacades.VocabularySharedFacade, useClass: FlatFacades.VocabularySharedFacade},
    // Viewer FlatFacades
    {provide: FlatFacades.FeedbackViewerFacade, useClass: FlatFacades.FeedbackViewerFacade},
    {provide: FlatFacades.SlideViewerFacade, useClass: FlatFacades.SlideViewerFacade},
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
